import { useMemo, useState } from 'react';
import { components, count, create, insertMultiple, search, type Orama } from '@orama/orama';

import type { FlowWithId } from '@/services/flows-service';
import type { MedicalModuleWithId } from '@/services/medical-modules-service';

// `text` holds every searchable field: Orama only counts a module as containing every query word
// when the words share a field, and "headache migraine" must match a name plus a criteria.
// `name` is indexed again on its own for its boost. nameTier decides the order first, so the boost
// only matters inside a tier, e.g. lifting a module with one of two query words in its name.
const schema = { name: 'string', text: 'string' } as const;
const boost = { name: 8, text: 1 };

// Shared by the index and the query, so "does this query contain a word" means the same to both.
const tokenizer = components.tokenizer.createTokenizer({ language: 'english' });

/** Ids of the modules matching `query`, most relevant first, or null when the query has no words and nothing is filtered out. */
export const useModuleSearch = (modules: MedicalModuleWithId[], flows: FlowWithId[], query: string): string[] | null => {
  // A query without any word (empty, punctuation, characters the tokenizer drops) filters nothing.
  const term = toWords(query, false).join(' ');
  const isSearching = term !== '';
  // Built only while searching, so snapshots arriving with an empty search box cost nothing.
  const index = useMemo(() => (isSearching ? buildIndex(modules, flows) : null), [modules, flows, isSearching]);

  const rankedIds = useMemo(() => {
    if (!index) return null;

    // Threshold 0: a module matches only when it contains every query word. No looser fallback,
    // because the matching list feeds bulk actions and a typo must not widen it.
    const { hits } = runSearch(index, term);

    // Name tier first, then Orama's score; equally relevant modules keep their incoming (name) order.
    const queryWords = term.split(' ');
    const byId = new Map(modules.map((module, position) => [module.id, { name: module.name, position }]));
    return hits
      .map((hit) => ({
        id: hit.id,
        score: hit.score,
        tier: nameTier(byId.get(hit.id)?.name, queryWords),
        position: byId.get(hit.id)?.position ?? 0,
      }))
      .sort((a, b) => a.tier - b.tier || b.score - a.score || a.position - b.position)
      .map((hit) => hit.id);
  }, [index, term, modules]);

  // Rows must not move under the pointer when a save re-ranks them, so the order only follows a new
  // ranking when the query changed. Derived state adjusted during render, as the React docs describe.
  const [shown, setShown] = useState({ term, rankedIds, ids: rankedIds });
  if (shown.rankedIds === rankedIds) return shown.ids;

  const ids = shown.term === term && shown.ids && rankedIds ? keepOrder(shown.ids, rankedIds) : rankedIds;
  setShown({ term, rankedIds, ids });
  return ids;
};

// `withCache` is off for the query, so half-typed words do not pile up in the tokenizer's cache.
const toWords = (text: string, withCache = true): string[] =>
  tokenizer.tokenize(toSearchText(text), undefined, undefined, withCache);

// The tokenizer keeps "-", "_" and "'" inside words, which would hide "organ" in "end-organ".
const toSearchText = (text: string): string => text.replace(/[-_']+/g, ' ');

const buildIndex = (modules: MedicalModuleWithId[], flows: FlowWithId[]) => {
  const flowNameById = new Map(flows.map((flow) => [flow.id, flow.name]));
  const index = create({ schema, components: { tokenizer } });
  assertSync(
    insertMultiple(
      index,
      modules.map((module) => {
        const flowNames = (module.flowIds ?? []).map((flowId) => flowNameById.get(flowId));
        // Orama rejects non-string values, so a malformed Firestore doc contributes only its string fields.
        const parts = [module.name, module.criteria, module.content, ...flowNames].filter((part) => typeof part === 'string');
        return { id: module.id, name: toIndexText(module.name), text: toIndexText(parts.join('\n')) };
      })
    )
  );
  return index;
};

const toIndexText = (value: unknown): string => (typeof value === 'string' ? toSearchText(value) : '');

// Orama only returns promises when the index has async hooks or components; this one has none.
const assertSync = <T>(result: T | Promise<T>): T => {
  if (result instanceof Promise) throw new Error('useModuleSearch requires a synchronous Orama index');
  return result;
};

// No relevance cutoff: threshold 0 already limits hits to modules containing every query word.
const runSearch = (index: Orama<typeof schema>, term: string) =>
  assertSync(search(index, { term, threshold: 0, boost, limit: count(index) }));

// Orama sums a score per matching word, so content mentioning "urine", "urinary", "urinalysis" can
// outscore the module actually named "URI". 0: the name holds every query word exactly;
// 1: the name holds them as prefixes ("uri" in "Urinary retention"); 2: matched elsewhere.
const nameTier = (name: unknown, queryWords: string[]): number => {
  const nameWords = typeof name === 'string' ? toWords(name) : [];
  if (queryWords.every((word) => nameWords.includes(word))) return 0;
  if (queryWords.every((word) => nameWords.some((nameWord) => nameWord.startsWith(word)))) return 1;
  return 2;
};

// The previous order for ids that still match, then new matches in ranked order.
const keepOrder = (previousIds: string[], rankedIds: string[]): string[] => {
  const ranked = new Set(rankedIds);
  const previous = new Set(previousIds);
  return [...previousIds.filter((id) => ranked.has(id)), ...rankedIds.filter((id) => !previous.has(id))];
};
